import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import PageBg from "../../../src/assets/images/auth-bg.png";
import { Link } from "react-router-dom";
import Logo from "../../../src/assets/images/logo.png";

/** Styles */
import "./verify-email.scss";

const OTP_LENGTH = 6;

const VerifyEmailPage = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(30);
  const inputRefs = useRef([]);

  useEffect(() => {
    document.title = "Verify OTP";
    inputRefs.current[0]?.focus(); // auto focus first box
  }, []);

  // countdown timer for resend
  useEffect(() => {
    if (resendTimer === 0) return;
    const timer = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleChange = (e, index) => {
    const val = e.target.value.replace(/\D/g, ""); // numbers only
    if (!val) return;

    const newOtp = [...otp];
    newOtp[index] = val.slice(-1); // only take last character
    setOtp(newOtp);
    setError("");

    // move to next box
    if (index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (otp[index]) {
        // clear current box
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // move to previous box
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;

    const newOtp = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((char, i) => (newOtp[i] = char));
    setOtp(newOtp);

    // focus last filled box
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleSubmit = () => {
    const otpValue = otp.join("");
    if (otpValue.length < OTP_LENGTH) {
      setError("Please enter the complete OTP.");
      return;
    }
    console.log("OTP submitted:", otpValue);
  };

  const handleResend = () => {
    setOtp(Array(OTP_LENGTH).fill(""));
    setError("");
    setResendTimer(30);
    inputRefs.current[0]?.focus();
    console.log("OTP resent");
  };

  return (
    <div className="otp-wrapper" style={{ backgroundImage: `url(${PageBg})` }}>
      <div className="otp-overlay" />

      <Container maxWidth="sm" className="otp-container">
        <Paper
          elevation={6}
          className={`otp-paper ${isMobile ? "mobile" : ""}`}
        >
          <Box className="otp-logo">
            <img src={Logo} alt="Logo" className={isMobile ? "mobile" : ""} />
          </Box>

          <Typography align="center" gutterBottom className="otp-title">
            Verify your email
          </Typography>

          <Typography align="center" className="otp-subtitle">
            We sent a 6-digit code to your email. <br /> Enter it below to
            continue.
          </Typography>

          <Box className="otp-inputs" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                variant="outlined"
                inputProps={{
                  maxLength: 1,
                  className: "otp-input-field",
                }}
                className={`otp-box ${error ? "error" : ""}`}
              />
            ))}
          </Box>

          {error && (
            <Typography align="center" className="otp-error">
              {error}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            size={isMobile ? "medium" : "large"}
            fullWidth
            className="otp-submit-btn"
            onClick={handleSubmit}
          >
            Verify
          </Button>

          <Typography align="center" className="otp-resend">
            Didn't receive the code?{" "}
            {resendTimer > 0 ? (
              <span className="otp-timer">Resend in {resendTimer}s</span>
            ) : (
              <button className="otp-resend-btn" onClick={handleResend}>
                Resend OTP
              </button>
            )}
          </Typography>

          <Typography align="center" className="otp-footer">
            <Link to="/login" style={{ color: theme.palette.primary.main }}>
              Back to Login
            </Link>
          </Typography>
        </Paper>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  width: state.layout.screenWidth,
});

export default withRouter(connect(mapStateToProps)(VerifyEmailPage));
